import { Injectable, inject } from '@angular/core';
import { AppActions } from '@navfleet/client/core/app';
import { LicenseFacade } from '@navfleet/client/license/domain/data-access';
import { License } from '@navfleet/client/license/domain/models';
import { NavAreaWarningApiQuery } from '@navfleet/client/map/safety/domain/models';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EMPTY, catchError, map, of, switchMap } from 'rxjs';
import { MapSafetyNavAreaApi } from '../api/map-safety-nav-area.api';
import { MapSafetyNavAreaActions, NavAreaWarningActions } from './map-safety-nav-area.actions';

@Injectable()
export class MapSafetyNavAreaEffects {
    private readonly actions$ = inject(Actions);
    private readonly api = inject(MapSafetyNavAreaApi);
    private readonly licenseFacade = inject(LicenseFacade);

    public readonly loadNavAreaData$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AppActions.loadAppData),
            this.licenseFacade.hasRequiredLicensesFilter({
                allRequired: [License.NavtorDataLayers],
                noneRequired: [License.Viewer],
            }),
            map(() => MapSafetyNavAreaActions.loadNavAreas())
        )
    );

    public readonly loadNavAreasResult$ = createEffect(() =>
        this.actions$.pipe(
            ofType(MapSafetyNavAreaActions.loadNavAreas),
            this.licenseFacade.hasRequiredLicensesFilter({
                allRequired: [License.NavtorDataLayers],
                noneRequired: [License.Viewer],
            }),
            switchMap(() =>
                this.api.getNavAreas().pipe(
                    map((navAreas) => MapSafetyNavAreaActions.loadNavAreasSuccess({ navAreas })),
                    catchError((error) => of(MapSafetyNavAreaActions.loadNavAreasFailure({ error })))
                )
            )
        )
    );

    public readonly loadNavAreaWarningsResult$ = createEffect(() =>
        this.actions$.pipe(
            ofType(NavAreaWarningActions.loadNavAreaWarnings),
            this.licenseFacade.hasRequiredLicensesFilter({
                allRequired: [License.NavtorDataLayers],
                noneRequired: [License.Viewer],
            }),
            switchMap(({ navAreaIds, dateRange }) => {
                if (!navAreaIds || (navAreaIds && navAreaIds.length === 0)) {
                    return EMPTY;
                }

                let query: NavAreaWarningApiQuery = { navAreaIds };

                if (dateRange) {
                    query = { ...query, from: dateRange.from.toISOString(), to: dateRange.to.toISOString() };
                }
                return this.api.getNavAreaWarnings(query).pipe(
                    map((warnings) => NavAreaWarningActions.loadNavAreaWarningSuccess({ warnings })),
                    catchError((error) => of(NavAreaWarningActions.loadNavAreaWarningFailure({ error })))
                );
            })
        )
    );
}
